import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { ScriptStep, StepStatus } from '@packages/global';
import { ScriptMode } from '@packages/global';
import { useTranslation } from 'react-i18next';

/**
 * StepNavigator 组件属性
 */
export interface StepNavigatorProps {
  /** 步骤列表 */
  steps: ScriptStep[];
  /** 当前激活的步骤编号 */
  activeStep: number;
  /** 步骤点击回调 */
  onStepClick: (stepNumber: number) => void;
  /** 脚本模式（灵活/严格） */
  scriptMode?: ScriptMode;
}

/**
 * 步骤名称 i18n key 映射（根据模式显示不同文本）
 * 严格模式下显示"提取"而不是"生成"
 */
const STEP_NAME_KEYS: Record<number, { flexible: string; strict: string }> = {
  1: { flexible: 'step.storySummary', strict: 'step.storySummary' },
  2: { flexible: 'step.visualStyle', strict: 'step.visualStyle' },
  3: { flexible: 'step.characters', strict: 'step.charactersStrict' },
  4: { flexible: 'step.props', strict: 'step.propsStrict' },
  5: { flexible: 'step.scenes', strict: 'step.scenesStrict' },
  6: { flexible: 'step.shots', strict: 'step.shotsStrict' },
};

/**
 * 获取步骤状态文本的 i18n key
 */
function getStepStatusKey(
  step: ScriptStep,
  scriptMode: ScriptMode
): string {
  // FILE_SCRIPT 和 PROJECT_SCRIPT 模式使用"提取"
  const hasScriptContent = scriptMode === ScriptMode.FILE_SCRIPT || scriptMode === ScriptMode.PROJECT_SCRIPT;

  switch (step.status) {
    case 'completed':
      return 'stepStatus.completed';
    case 'in_progress':
      return hasScriptContent ? 'stepStatus.extracting' : 'stepStatus.inProgress';
    case 'needs_review':
      return 'stepStatus.needsReview';
    case 'pending':
    default:
      return hasScriptContent ? 'stepStatus.pendingExtract' : 'stepStatus.pending';
  }
}

/**
 * 获取步骤状态文本颜色
 */
function getStatusTextColor(status: StepStatus): string {
  switch (status) {
    case 'completed':
      return 'text-success';
    case 'in_progress':
      return 'text-primary';
    case 'needs_review':
      return 'text-warning';
    case 'pending':
    default:
      return 'text-gray-400';
  }
}

/**
 * 步骤状态图标组件
 * 
 * 根据步骤状态显示不同的图标：
 * - completed: 绿色勾选图标
 * - in_progress: 旋转的 loader 图标
 * - needs_review: 黄色圆点
 * - pending: 灰色圆点
 */
function StepStatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="w-[18px] h-[18px] text-success" />;
    case 'in_progress':
      return (
        <div className="w-[18px] h-[18px] flex items-center justify-center">
          <ArrowPathIcon className="w-4 h-4 text-primary animate-spin" />
        </div>
      );
    case 'needs_review':
      return (
        <div className="w-[18px] h-[18px] flex items-center justify-center">
          <div className="w-2.5 h-2.5 bg-warning rounded-full" />
        </div>
      );
    case 'pending':
    default:
      return (
        <div className="w-[18px] h-[18px] flex items-center justify-center">
          <div className="w-2 h-2 bg-gray-400 rounded-full" />
        </div>
      );
  }
}

/**
 * 步骤项组件
 * 
 * 设计参考：Pencil 设计中的 Step1-Active, Step2 等节点
 * - padding: [12, 16] (py-3 px-4)
 * - gap: 12px
 * - 激活状态: fill=#1A1A1D, 左侧边框 $primary (3px)
 */
interface StepItemProps {
  step: ScriptStep;
  isActive: boolean;
  onClick: () => void;
  scriptMode: ScriptMode;
}

function StepItem({ step, isActive, onClick, scriptMode }: StepItemProps) {
  const { t } = useTranslation('script');
  const stepNameKeys = STEP_NAME_KEYS[step.stepNumber];
  // FILE_SCRIPT 和 PROJECT_SCRIPT 模式使用 strict 名称
  const hasScriptContent = scriptMode === ScriptMode.FILE_SCRIPT || scriptMode === ScriptMode.PROJECT_SCRIPT;
  const displayName = stepNameKeys
    ? t(hasScriptContent ? stepNameKeys.strict : stepNameKeys.flexible)
    : step.name;
  const statusText = t(getStepStatusKey(step, scriptMode));
  const statusColor = getStatusTextColor(step.status);

  // 激活状态的边框颜色：完成时用绿色，否则用主色
  const activeBorderColor = step.status === 'completed'
    ? 'border-l-success'
    : 'border-l-primary';

  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 py-3 px-4 text-left transition-colors cursor-pointer
        ${isActive
          ? `bg-bg-elevated border-l-[3px] ${activeBorderColor}`
          : 'border-l-[3px] border-l-transparent hover:bg-bg-surface'
        }
      `}
    >
      <StepStatusIcon status={step.status} />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span
          className={`text-[13px] font-medium truncate ${isActive ? 'text-gray-50' : 'text-gray-200'
            }`}
        >
          {displayName}
        </span>
        <span className={`text-[11px] ${statusColor}`}>
          {statusText}
        </span>
      </div>
    </button>
  );
}

/**
 * StepNavigator 组件
 * 
 * 步骤导航器，用于显示脚本生成的 6 个步骤及其状态。
 * 
 * 设计参考：Pencil 设计中的 StepsSection 节点
 * - layout: vertical
 * - padding: [16, 0]
 * - width: fill_container
 * - height: fill_container
 * 
 * 功能特性：
 * 1. 显示 6 个步骤的名称和状态
 * 2. 支持点击切换步骤
 * 3. 根据脚本模式显示不同的步骤名称（灵活模式显示"生成"，严格模式显示"提取"）
 * 4. 步骤状态包括：pending, in_progress, completed, needs_review
 * 
 * Requirements: 3.2, 3.3, 3.4, 3.7, 3.8
 */
export default function StepNavigator({
  steps,
  activeStep,
  onStepClick,
  scriptMode = ScriptMode.PROMPT,
}: StepNavigatorProps) {
  return (
    <nav className="flex flex-col py-4 h-full overflow-y-auto">
      {steps.map((step) => (
        <StepItem
          key={step.stepNumber}
          step={step}
          isActive={step.stepNumber === activeStep}
          onClick={() => onStepClick(step.stepNumber)}
          scriptMode={scriptMode}
        />
      ))}
    </nav>
  );
}
